import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Switch, TextField, Button, Grid, Alert, Collapse,
  List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import { RequestQuote as InvoiceIcon, AccountBalance as BankIcon } from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';

/**
 * Untersektion „Rechnungen (Firmenkunden)" innerhalb der gemeinsamen Sektion
 * „Quittungen & Rechnungen" (ReceiptSettingsSection). Die Erst-Einrichtung
 * läuft über den gemeinsamen Assistenten (Schritt „Rechnungen"): Firmen-,
 * Steuer- und Versanddaten werden geteilt (rechnungskunden_konzept.md 4.6);
 * hier stehen nur die rechnungsspezifischen Werte zum Nachschärfen.
 * SMTP ist KEINE Aktivierungs-Voraussetzung — Rechnungen sind im Portal
 * abrufbar; der (Auto-)Versand ist pro Rechnungskunde einstellbar.
 * `onEnabledChange` meldet den Aktiv-Status an die Einstellungsseite
 * (Sperre von „Fahrpreis nach App-Fahrt erfassen").
 */
const InvoiceSettingsSection = ({ canManage = true, onEnabledChange }) => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [form, setForm] = useState({
    numberPrefix: 'R-', monthlyRunDay: 1, defaultPaymentTermsDays: 14,
    iban: '', bic: '', holder: '', footerNote: ''
  });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'appSettings', 'invoices'), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setEnabled(d.invoicesEnabled === true);
      if (onEnabledChange) onEnabledChange(d.invoicesEnabled === true);
      setForm({
        numberPrefix: d.numberPrefix || 'R-',
        monthlyRunDay: d.monthlyRunDay || 1,
        defaultPaymentTermsDays: d.defaultPaymentTermsDays ?? 14,
        iban: d.bank?.iban || '', bic: d.bank?.bic || '', holder: d.bank?.holder || '',
        footerNote: d.footerNote || ''
      });
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [onEnabledChange]);

  const setField = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleToggle = async () => {
    if (!enabled) {
      // Aktivierung erzwingt serverseitig requireDriverFareInput = an
      // (Rechnungen enthalten nur echte Fahrpreise) — vorher ankündigen.
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        'Für Rechnungen werden echte Fahrpreise benötigt — „Fahrpreis nach App-Fahrt erfassen" wird mit aktiviert und gesperrt.'
      )) return;
    }
    setBusy(true); setFeedback(null);
    try {
      await httpsCallable(functions, 'configureInvoices')({ enabled: !enabled });
      setFeedback({ severity: 'success', message: !enabled ? 'Rechnungserstellung aktiviert.' : 'Rechnungserstellung deaktiviert.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.message || 'Einstellung konnte nicht gespeichert werden.' });
    } finally { setBusy(false); }
  };

  const handleSave = async () => {
    setSaving(true); setFeedback(null);
    try {
      await httpsCallable(functions, 'configureInvoices')({
        numberPrefix: form.numberPrefix,
        monthlyRunDay: Number(form.monthlyRunDay) || 1,
        defaultPaymentTermsDays: Number(form.defaultPaymentTermsDays) || 14,
        bank: { iban: form.iban, bic: form.bic, holder: form.holder },
        footerNote: form.footerNote
      });
      setFeedback({ severity: 'success', message: 'Rechnungs-Einstellungen gespeichert.' });
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.message || 'Speichern fehlgeschlagen.' });
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <>
      {feedback && (
        <Alert severity={feedback.severity} sx={{ mx: 3, mt: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <List disablePadding>
        <ListItem
          secondaryAction={
            <Switch edge="end" checked={enabled} onChange={handleToggle} disabled={!canManage || busy} color="primary" />
          }
        >
          <ListItemIcon><InvoiceIcon /></ListItemIcon>
          <ListItemText
            primary="Rechnungen für Firmenkunden erstellen"
            secondary={enabled
              ? `Monatslauf am ${form.monthlyRunDay}. — Einzelrechnungen jederzeit über „Rechnungskunden“`
              : 'Aktivierung braucht nur die Firmen-Stammdaten (Assistent oben) — Rechnungen sind im Portal abrufbar, E-Mail-Versand ist optional und pro Rechnungskunde steuerbar'}
          />
        </ListItem>
      </List>

      <Collapse in={enabled}>
        <Box sx={{ px: 3, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField fullWidth size="small" label="Rechnungsnummer-Präfix" value={form.numberPrefix} onChange={setField('numberPrefix')} disabled={!canManage} placeholder="R-" helperText={`z. B. „${form.numberPrefix || 'R-'}2026-000001“`} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField fullWidth size="small" type="number" label="Monatslauf am Tag" value={form.monthlyRunDay} onChange={setField('monthlyRunDay')} disabled={!canManage} InputProps={{ inputProps: { min: 1, max: 28 } }} helperText="Sammelrechnungs-Lauf (1–28)" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" type="number" label="Zahlungsziel (Tage)" value={form.defaultPaymentTermsDays} onChange={setField('defaultPaymentTermsDays')} disabled={!canManage} InputProps={{ inputProps: { min: 0, max: 90 } }} helperText="Standard — je Kunde änderbar" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BankIcon fontSize="small" color="action" /> Bankverbindung
                <Typography variant="caption" color="text.secondary" component="span">
                  — für den Zahlungshinweis auf der Rechnung
                </Typography>
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField fullWidth size="small" label="IBAN" placeholder="DE00 0000 0000 0000 0000 00" value={form.iban} onChange={setField('iban')} disabled={!canManage} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth size="small" label="BIC" value={form.bic} onChange={setField('bic')} disabled={!canManage} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Kontoinhaber" value={form.holder} onChange={setField('holder')} disabled={!canManage} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" multiline minRows={2} label="Fußzeile / Zahlungshinweis (optional)" value={form.footerNote} onChange={setField('footerNote')} disabled={!canManage} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button variant="contained" onClick={handleSave} disabled={!canManage || saving}>
                {saving ? 'Speichert…' : 'Rechnungs-Einstellungen speichern'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </>
  );
};

export default InvoiceSettingsSection;
